import { v4 as uuidv4 } from 'uuid';
import { db } from './schema';
import type { Loan } from '@/types';
import { DEFAULT_LOAN_CONFIG } from '@/types';

/**
 * Repository for the single Loan record (v1 is single-loan scope — see
 * spec fixed decision #1). All reads/writes to the `loans` table go
 * through here so the rest of the app never touches Dexie directly for
 * loan data.
 */

const LOAN_SLUG = 'loan-primary' as const;

function nowIso(): string {
  return new Date().toISOString();
}

/**
 * Returns the primary loan record, or undefined if none has been created
 * yet (e.g. on very first app launch before onboarding completes).
 */
export async function getLoan(): Promise<Loan | undefined> {
  return db.loans.get({ slug: LOAN_SLUG });
}

/**
 * Creates the initial draft/expected loan record. Should only be called
 * once (during onboarding); throws if a loan already exists, so callers
 * don't accidentally create a second one.
 */
export async function createDraftLoan(
  input: Pick<
    Loan,
    'lender' | 'sanctionAmount' | 'approvedAmount' | 'originalAnnualRatePercent' | 'originalTenureMonths' | 'originalEmiAtFullDisbursement'
  > &
    Partial<
      Pick<
        Loan,
        | 'loanAccountNumber'
        | 'loanStartDate'
        | 'regularEmiStartDate'
        | 'notes'
        | 'emiDayOfMonth'
        | 'isApprovedAmountManuallySet'
        | 'isEmiManuallyOverridden'
        | 'regularEmiAmount'
        | 'repaymentArrangement'
        | 'lenderReported'
        | 'dayCountConvention'
        | 'preEmiInterestTreatment'
      >
    >
): Promise<Loan> {
  const existing = await getLoan();
  if (existing) {
    throw new Error('A loan record already exists. This app supports a single loan in v1.');
  }

  const timestamp = nowIso();
  const loan: Loan = {
    id: uuidv4(),
    slug: LOAN_SLUG,
    status: 'draft_expected',
    createdAt: timestamp,
    updatedAt: timestamp,

    lender: input.lender,
    loanAccountNumber: input.loanAccountNumber,
    sanctionAmount: input.sanctionAmount,
    approvedAmount: input.approvedAmount,
    isApprovedAmountManuallySet: input.isApprovedAmountManuallySet,
    originalAnnualRatePercent: input.originalAnnualRatePercent,
    originalTenureMonths: input.originalTenureMonths,
    originalEmiAtFullDisbursement: input.originalEmiAtFullDisbursement,
    isEmiManuallyOverridden: input.isEmiManuallyOverridden,
    emiDayOfMonth: input.emiDayOfMonth ?? DEFAULT_LOAN_CONFIG.emiDayOfMonth,
    regularEmiStartDate: input.regularEmiStartDate,
    regularEmiAmount: input.regularEmiAmount,
    repaymentArrangement: input.repaymentArrangement,
    lenderReported: input.lenderReported,
    loanStartDate: input.loanStartDate,

    dayCountConvention: input.dayCountConvention ?? DEFAULT_LOAN_CONFIG.dayCountConvention,
    preEmiInterestTreatment: input.preEmiInterestTreatment ?? DEFAULT_LOAN_CONFIG.preEmiInterestTreatment,
    emiRoundingRule: DEFAULT_LOAN_CONFIG.emiRoundingRule,

    notes: input.notes,
  };

  await db.loans.add(loan);
  return loan;
}

/**
 * Updates the loan record. This is a direct mutation (not event-sourced)
 * because the Loan record itself represents "current understanding of
 * the base terms" — per spec, legitimate CHANGES over time (rate
 * changes, disbursements, EMI/tenure changes) are modelled as events, not
 * as edits here. Editing this record directly is intended for:
 *   (a) correcting a data-entry mistake in the draft terms, or
 *   (b) promoting a draft loan to lic_confirmed once real sanction
 *       terms are known (still a form of "this is what we now know the
 *       base terms to be", not a mid-life change).
 * The updatedAt timestamp is bumped on every call so the UI can show
 * "last edited" if useful.
 */
export async function updateLoan(id: string, patch: Partial<Omit<Loan, 'id' | 'slug' | 'createdAt'>>): Promise<void> {
  await db.loans.update(id, { ...patch, updatedAt: nowIso() } as Partial<Loan>);
}

/**
 * Convenience helper to flip a draft loan to lic_confirmed status once
 * the user has entered real sanction-letter/KFS terms. Does not
 * otherwise change any field — the caller is expected to have already
 * updated the base terms via updateLoan() with the real figures.
 */
export async function markLoanAsLicConfirmed(id: string): Promise<void> {
  await updateLoan(id, { status: 'lic_confirmed' });
}
